import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import api from '../utils/api';
import CardPreview from '../components/CardPreview';
import toast from 'react-hot-toast';

// Sales Card v1 — form creates/updates the horse's public listing, card prints with live QR.
// Labeled deferrals (v2): color/logo customizer, landscape layout, At-Stud fee field styling.

const BURGUNDY = '#340925';
const GOLD = '#BA7517';
const CREAM = '#FBF8F3';

/*
 * Stall card templates.
 *
 * IMPORTANT:
 * These are NOT 12 separate card implementations.
 * They share reusable layout families, while each template supplies
 * its own palette and visual treatment.
 *
 * Families:
 *   classic
 *   modern
 *   editorial
 *   bold
 */
const STALL_CARD_TEMPLATES = [
  {
    id: 'classic-emerald',
    name: 'Classic Emerald',
    family: 'classic',
    variant: 'classic-ornate',
    palette: {
      paper: '#FBF8F0',
      dark: '#174A36',
      accent: '#C4963A',
      banner: '#7A102A'
    }
  },
  {
    id: 'modern-silver',
    name: 'Modern Silver',
    family: 'modern',
    variant: 'modern-grid',
    palette: {
      paper: '#F7F7F5',
      dark: '#202426',
      accent: '#A9ADB0',
      banner: '#7A102A'
    }
  },
  {
    id: 'editorial-ivory',
    name: 'Editorial Ivory',
    family: 'editorial',
    variant: 'editorial-asym',
    palette: {
      paper: '#F5EFE4',
      dark: '#23201C',
      accent: '#B39055',
      banner: '#7A102A'
    }
  },
  {
    id: 'bold-navy',
    name: 'Bold Navy',
    family: 'bold',
    variant: 'bold-dark',
    palette: {
      paper: '#0E2034',
      dark: '#0E2034',
      accent: '#B88952',
      banner: '#7A102A'
    }
  },
  {
    id: 'heritage-gold',
    name: 'Heritage Gold',
    family: 'classic',
    variant: 'heritage-ornate',
    palette: {
      paper: '#EBD9B4',
      dark: '#263C2E',
      accent: '#9B6D24',
      banner: '#7A102A'
    }
  },
  {
    id: 'sporthorse-clean',
    name: 'Sporthorse Clean',
    family: 'modern',
    variant: 'sporthorse-clean',
    palette: {
      paper: '#FAFAF8',
      dark: '#1E2B36',
      accent: '#8C969D',
      banner: '#7A102A'
    }
  },
  {
    id: 'sage-luxe',
    name: 'Sage Luxe',
    family: 'classic',
    variant: 'sage-luxe',
    palette: {
      paper: '#F4F1E6',
      dark: '#59634A',
      accent: '#B59B5A',
      banner: '#7A102A'
    }
  },
  {
    id: 'black-silver',
    name: 'Black & Silver',
    family: 'bold',
    variant: 'black-silver',
    palette: {
      paper: '#121212',
      dark: '#121212',
      accent: '#BFC2C4',
      banner: '#7A102A'
    }
  },
  {
    id: 'desert-copper',
    name: 'Desert Copper',
    family: 'bold',
    variant: 'desert-copper',
    palette: {
      paper: '#E8D5AF',
      dark: '#56331E',
      accent: '#A55A2A',
      banner: '#7A102A'
    }
  },
  {
    id: 'monogram-blush',
    name: 'Monogram Blush',
    family: 'editorial',
    variant: 'monogram',
    palette: {
      paper: '#F8ECEA',
      dark: '#513B3C',
      accent: '#B98F87',
      banner: '#7A102A'
    }
  },
  {
    id: 'catalog-blackout',
    name: 'Catalog Blackout',
    family: 'editorial',
    variant: 'catalog',
    palette: {
      paper: '#151515',
      dark: '#151515',
      accent: '#D0D0CD',
      banner: '#7A102A'
    }
  },
  {
    id: 'contemporary-plum',
    name: 'Contemporary Plum',
    family: 'modern',
    variant: 'contemporary-geo',
    palette: {
      paper: '#F4F1F2',
      dark: '#512640',
      accent: '#8D7181',
      banner: '#7A102A'
    }
  }
];

function StallTemplateThumb({ template }) {
  const p = template.palette;
  const v = template.variant;

  const darkCard = [
    'bold-dark',
    'black-silver',
    'catalog'
  ].includes(v);

  const text = darkCard ? '#F5F2EC' : p.dark;
  const muted = darkCard ? '#D7D3CC' : '#504B45';

  const qr = (
    <div style={{
      width: 29,
      height: 29,
      background:
        'repeating-linear-gradient(45deg,#111 0,#111 2px,#fff 2px,#fff 4px)',
      border: '3px solid white',
      outline: `1px solid ${p.accent}`,
      flexShrink: 0
    }} />
  );

  const banner = (
    <div style={{
      display: 'inline-block',
      background: p.banner,
      color: '#fff',
      padding: '2px 7px',
      fontSize: 4.6,
      letterSpacing: .65,
      whiteSpace: 'nowrap'
    }}>
      THIS HORSE IS FOR SALE
    </div>
  );

  const pedigree = (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '18px 1fr',
      border: `1px solid ${p.accent}`,
      fontSize: 3.2,
      lineHeight: 1.25,
      marginTop: 3
    }}>
      <b style={{ padding: 2, borderRight: `1px solid ${p.accent}` }}>SIRE</b>
      <span style={{ padding: 2 }}>THE ICE AGE MAN</span>
      <b style={{ padding: 2, borderTop: `1px solid ${p.accent}`, borderRight: `1px solid ${p.accent}` }}>DAM</b>
      <span style={{ padding: 2, borderTop: `1px solid ${p.accent}` }}>AM HONORABLE ISABELLA</span>
    </div>
  );

  const footer = (
    <div style={{
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 18,
      background:
        ['modern-grid', 'sporthorse-clean', 'monogram'].includes(v)
          ? '#D9D9D7'
          : p.dark,
      color:
        ['modern-grid', 'sporthorse-clean', 'monogram'].includes(v)
          ? '#222'
          : '#fff',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1.2fr',
      alignItems: 'center',
      textAlign: 'center',
      fontSize: 2.8
    }}>
      <span>EMERGENCY</span>
      <span>VETERINARIAN</span>
      <span>LISTED VIA CONNECTED RIDER</span>
    </div>
  );

  const base = {
    height: 108,
    position: 'relative',
    overflow: 'hidden',
    background: p.paper,
    border: `2px solid ${p.dark}`,
    color: text,
    marginBottom: 7
  };

  /* 1 — CLASSIC EMERALD */
  if (v === 'classic-ornate') return (
    <div style={base}>
      <div style={{ position:'absolute', inset:4, border:`1px solid ${p.accent}` }} />
      <div style={{ position:'absolute', left:7, top:6, width:'68%', textAlign:'center' }}>
        <div style={{ fontFamily:'Georgia,serif', fontSize:5, letterSpacing:1 }}>CHARMED ARABIANS</div>
        <div style={{ margin:'4px 0' }}>{banner}</div>
        <div style={{ fontFamily:'Georgia,serif', fontSize:11 }}>Glitterati Gold</div>
        <div style={{ fontSize:3.3, color:muted }}>APRIL 2023 · PALOMINO · GELDING</div>
        {pedigree}
      </div>
      <div style={{ position:'absolute', right:8, top:10, width:'22%', textAlign:'center', fontSize:3.5 }}>
        <b>INTERESTED?</b><div style={{ margin:'5px auto' }}>{qr}</div>
      </div>
      {footer}
    </div>
  );

  /* 2 — MODERN SILVER */
  if (v === 'modern-grid') return (
    <div style={{ ...base, fontFamily:'Arial,sans-serif' }}>
      <div style={{ position:'absolute', left:9, top:8, width:'65%' }}>
        <div style={{ fontSize:5, letterSpacing:1 }}>CHARMED ARABIANS</div>
        <div style={{ margin:'7px 0 5px' }}>{banner}</div>
        <div style={{ fontSize:12, fontWeight:800 }}>Glitterati Gold</div>
        <div style={{ fontSize:3.2, margin:'3px 0' }}>APRIL 2023 · PALOMINO · GELDING</div>
        {pedigree}
      </div>
      <div style={{ position:'absolute', right:7, top:8, bottom:22, width:'22%', borderLeft:`1px solid ${p.accent}`, textAlign:'center', paddingTop:6, fontSize:3.5 }}>
        <b>INTERESTED?</b><div style={{ margin:'5px auto' }}>{qr}</div>
      </div>
      {footer}
    </div>
  );

  /* 3 — EDITORIAL IVORY */
  if (v === 'editorial-asym') return (
    <div style={base}>
      <div style={{ position:'absolute', left:8, top:7, width:'66%' }}>
        <div style={{ fontSize:7, lineHeight:.9 }}>CHARMED<br/>ARABIANS</div>
        <div style={{ margin:'6px 0 4px' }}>{banner}</div>
        <div style={{ fontFamily:'Georgia,serif', fontStyle:'italic', fontSize:13, lineHeight:.9 }}>Glitterati<br/>Gold</div>
        <div style={{ fontSize:3.2, marginTop:4 }}>APRIL 2023 · PALOMINO · GELDING</div>
      </div>
      <div style={{ position:'absolute', left:9, bottom:24, width:'58%' }}>{pedigree}</div>
      <div style={{ position:'absolute', right:5, top:0, width:'27%', height:'82%', borderLeft:`1px solid ${p.accent}`, transform:'skewX(-6deg)' }} />
      <div style={{ position:'absolute', right:9, top:12, textAlign:'center', fontSize:3.5 }}><b>INTERESTED?</b><div style={{ marginTop:7 }}>{qr}</div></div>
      {footer}
    </div>
  );

  /* 4 — BOLD NAVY */
  if (v === 'bold-dark') return (
    <div style={{ ...base, fontFamily:'Arial,sans-serif' }}>
      <div style={{ position:'absolute', left:8, top:7, width:'65%' }}>
        <div style={{ fontSize:5, letterSpacing:1 }}>CHARMED ARABIANS</div>
        <div style={{ margin:'5px 0' }}>{banner}</div>
        <div style={{ fontSize:15, fontWeight:900, lineHeight:.85 }}>GLITTERATI<br/>GOLD</div>
        <div style={{ fontSize:3.2, marginTop:5 }}>APRIL 2023 · PALOMINO · GELDING</div>
        {pedigree}
      </div>
      <div style={{ position:'absolute', right:7, top:7, width:'23%', textAlign:'center', border:`1px solid ${p.accent}`, padding:'6px 3px', fontSize:3.5 }}>
        <b>INTERESTED?</b><div style={{ margin:'6px auto 0' }}>{qr}</div>
      </div>
      {footer}
    </div>
  );

  /* 5 — HERITAGE GOLD */
  if (v === 'heritage-ornate') return (
    <div style={base}>
      <div style={{ position:'absolute', inset:3, border:`2px double ${p.accent}` }} />
      <div style={{ position:'absolute', left:7, top:5, width:'68%', textAlign:'center' }}>
        <div style={{ fontSize:6 }}>❦ CHARMED ARABIANS ❦</div>
        <div style={{ margin:'4px 0' }}>{banner}</div>
        <div style={{ fontFamily:'Georgia,serif', fontSize:12 }}>Glitterati Gold</div>
        <div style={{ fontSize:3.2 }}>APRIL 2023 · PALOMINO · GELDING</div>
        {pedigree}
      </div>
      <div style={{ position:'absolute', right:8, top:10, textAlign:'center', fontSize:3.5 }}><b>INTERESTED?</b><div style={{ marginTop:6 }}>{qr}</div></div>
      {footer}
    </div>
  );

  /* 6 — SPORTHORSE CLEAN */
  if (v === 'sporthorse-clean') return (
    <div style={{ ...base, fontFamily:'Arial,sans-serif', border:`1px solid ${p.accent}` }}>
      <div style={{ position:'absolute', left:10, top:8, width:'64%' }}>
        <div style={{ fontSize:6, fontWeight:700 }}>CA&nbsp;&nbsp; CHARMED ARABIANS</div>
        <div style={{ margin:'7px 0 4px' }}>{banner}</div>
        <div style={{ fontSize:12, fontWeight:800 }}>Glitterati Gold</div>
        <div style={{ fontSize:3.2, margin:'4px 0 5px' }}>APRIL 2023 · PALOMINO · GELDING · HALF ARABIAN</div>
        {pedigree}
      </div>
      <div style={{ position:'absolute', right:8, top:8, width:'22%', borderLeft:`2px solid ${p.dark}`, paddingLeft:6, textAlign:'center', fontSize:3.5 }}>
        <b>INTERESTED?</b><div style={{ margin:'7px auto' }}>{qr}</div>
      </div>
      {footer}
    </div>
  );

  /* 7 — SAGE LUXE */
  if (v === 'sage-luxe') return (
    <div style={base}>
      <div style={{ position:'absolute', inset:5, border:`1px solid ${p.accent}` }} />
      <div style={{ position:'absolute', left:8, top:7, width:'66%', textAlign:'center' }}>
        <div style={{ fontSize:5, letterSpacing:1.3 }}>— CHARMED ARABIANS —</div>
        <div style={{ margin:'6px 0' }}>{banner}</div>
        <div style={{ fontFamily:'Georgia,serif', fontStyle:'italic', fontSize:12 }}>Glitterati Gold</div>
        <div style={{ fontSize:3.2, marginTop:4 }}>APRIL 2023 · PALOMINO · GELDING</div>
        {pedigree}
      </div>
      <div style={{ position:'absolute', right:7, top:8, bottom:23, width:'23%', background:p.dark, color:'#fff', textAlign:'center', paddingTop:7, fontSize:3.5 }}>
        <b>INTERESTED?</b><div style={{ margin:'6px auto' }}>{qr}</div>
      </div>
      {footer}
    </div>
  );

  /* 8 — BLACK & SILVER */
  if (v === 'black-silver') return (
    <div style={base}>
      <div style={{ position:'absolute', inset:5, border:`1px solid ${p.accent}` }} />
      <div style={{ position:'absolute', left:8, top:7, width:'65%', textAlign:'center' }}>
        <div style={{ fontSize:5, letterSpacing:1.3 }}>CHARMED ARABIANS</div>
        <div style={{ margin:'5px 0' }}>{banner}</div>
        <div style={{ fontFamily:'Georgia,serif', fontSize:12 }}>GLITTERATI GOLD</div>
        <div style={{ fontSize:3.2, margin:'4px 0' }}>APRIL 2023 · PALOMINO · GELDING</div>
        {pedigree}
      </div>
      <div style={{ position:'absolute', right:8, top:10, textAlign:'center', fontSize:3.5 }}><b>INTERESTED?</b><div style={{ marginTop:7 }}>{qr}</div></div>
      {footer}
    </div>
  );

  /* 9 — DESERT COPPER */
  if (v === 'desert-copper') return (
    <div style={base}>
      <div style={{ position:'absolute', left:7, top:6, width:'67%' }}>
        <div style={{ fontSize:6, letterSpacing:1 }}>◆ CHARMED ARABIANS ◆</div>
        <div style={{ margin:'5px 0' }}>{banner}</div>
        <div style={{ fontFamily:'Georgia,serif', fontSize:12 }}>GLITTERATI GOLD</div>
        <div style={{ fontSize:3.2, margin:'4px 0' }}>APRIL 2023 · PALOMINO · GELDING</div>
        {pedigree}
      </div>
      <div style={{ position:'absolute', right:7, top:8, bottom:23, width:'22%', border:`2px solid ${p.accent}`, textAlign:'center', paddingTop:6, fontSize:3.5 }}>
        <b>INTERESTED?</b><div style={{ margin:'6px auto' }}>{qr}</div>
      </div>
      {footer}
    </div>
  );

  /* 10 — MONOGRAM BLUSH */
  if (v === 'monogram') return (
    <div style={base}>
      <div style={{ position:'absolute', left:5, top:8, fontFamily:'Georgia,serif', fontSize:30, opacity:.12 }}>CA</div>
      <div style={{ position:'absolute', left:37, top:8, width:'47%' }}>
        <div style={{ fontSize:5, letterSpacing:1 }}>CHARMED ARABIANS</div>
        <div style={{ margin:'6px 0' }}>{banner}</div>
        <div style={{ fontFamily:'Georgia,serif', fontStyle:'italic', fontSize:11 }}>Glitterati Gold</div>
        <div style={{ fontSize:3.2, margin:'4px 0' }}>APRIL 2023 · PALOMINO</div>
        {pedigree}
      </div>
      <div style={{ position:'absolute', right:7, top:8, width:'22%', textAlign:'center', fontSize:3.5 }}><b>INTERESTED?</b><div style={{ margin:'7px auto' }}>{qr}</div></div>
      {footer}
    </div>
  );

  /* 11 — CATALOG BLACKOUT */
  if (v === 'catalog') return (
    <div style={base}>
      <div style={{ position:'absolute', left:8, top:7, width:'63%' }}>
        <div style={{ fontSize:5, letterSpacing:1.2 }}>CHARMED ARABIANS</div>
        <div style={{ margin:'5px 0' }}>{banner}</div>
        <div style={{ fontFamily:'Georgia,serif', fontSize:14, lineHeight:.9 }}>GLITTERATI<br/>GOLD</div>
        <div style={{ fontSize:3.2, marginTop:4 }}>APRIL 2023 · PALOMINO · GELDING</div>
        <div style={{ width:'72%', marginTop:5 }}>{pedigree}</div>
      </div>
      <div style={{ position:'absolute', right:6, top:6, bottom:22, width:'26%', borderLeft:`1px solid ${p.accent}`, padding:'5px', textAlign:'center', fontSize:3.5 }}>
        <b>FOR SALE</b><div style={{ margin:'7px auto' }}>{qr}</div><span>SCAN FOR MORE</span>
      </div>
      {footer}
    </div>
  );

  /* 12 — CONTEMPORARY PLUM */
  return (
    <div style={{ ...base, fontFamily:'Arial,sans-serif' }}>
      <div style={{ position:'absolute', left:8, top:7, width:'63%' }}>
        <div style={{ fontSize:5, letterSpacing:1.2 }}>CHARMED ARABIANS</div>
        <div style={{ margin:'6px 0' }}>{banner}</div>
        <div style={{ fontFamily:'Georgia,serif', fontSize:11 }}>GLITTERATI GOLD</div>
        <div style={{ fontSize:3.2, margin:'4px 0' }}>APRIL 2023 · PALOMINO · GELDING</div>
        {pedigree}
      </div>
      <div style={{
        position:'absolute',
        right:0,
        top:0,
        bottom:18,
        width:'30%',
        background:p.dark,
        clipPath:'polygon(24% 0,100% 0,100% 100%,24% 100%,0 50%)',
        color:'#fff',
        textAlign:'center',
        padding:'13px 4px 0 11px',
        fontSize:3.5
      }}>
        <b>INTERESTED?</b><div style={{ margin:'7px auto' }}>{qr}</div>
      </div>
      {footer}
    </div>
  );
}



function ClassicEmeraldCard({ horse, card, banner, foaled, publicUrl }) {
  const nameLen = (horse && horse.name ? horse.name.length : 0);
  const nameFontSize = nameLen > 26 ? 34 : nameLen > 21 ? 40 : nameLen > 17 ? 47 : 57;
  const green = card.dark_color || '#174A36';
  const gold = card.gold_color || '#C4963A';
  const burgundy = card.sale_color || '#7A102A';
  const paper = card.paper_color || '#FBF8F0';

  const meta = [
    foaled,
    horse.color,
    horse.gender,
    horse.breed
  ].filter(Boolean).join('  •  ').toUpperCase();

  return (
    <div
      className="sales-card"
      style={{
        width: 1056,
        height: 816,
        boxSizing: 'border-box',
        background: paper,
        border: `12px solid ${green}`,
        padding: 10,
        color: green,
        fontFamily: 'Georgia, "Times New Roman", serif',
        overflow: 'hidden'
      }}
    >
      <div
        style={{
          height: '100%',
          border: `2px solid ${gold}`,
          boxSizing: 'border-box',
          display: 'grid',
          gridTemplateRows: '1fr 112px',
          position: 'relative'
        }}
      >

        {/* subtle ornamental corners */}
        <div style={{ position:'absolute', left:14, top:8, color:gold, fontSize:28 }}>◆</div>
        <div style={{ position:'absolute', right:14, top:8, color:gold, fontSize:28 }}>◆</div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '68% 32%',
            minHeight: 0
          }}
        >

          {/* LEFT SIDE */}
          <section
            style={{
              padding: '34px 46px 24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center'
            }}
          >

            {/* FARM BRANDING */}
            <div
              style={{
                minHeight: 82,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {card.farm_logo_url ? (
                <>
                  <img
                    src={card.farm_logo_url}
                    alt={card.farm_name || 'Farm logo'}
                    style={{
                      maxWidth: 340,
                      maxHeight: 58,
                      objectFit: 'contain'
                    }}
                  />

                  {card.farm_name && (
                    <div
                      style={{
                        marginTop: 5,
                        fontSize: 13,
                        letterSpacing: 3,
                        textTransform: 'uppercase'
                      }}
                    >
                      {card.farm_name}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div style={{ color: gold, fontSize: 18 }}>
                    ◆
                  </div>

                  <div
                    style={{
                      marginTop: 2,
                      fontSize: 30,
                      letterSpacing: 4,
                      textTransform: 'uppercase'
                    }}
                  >
                    {card.farm_name || horse.barn_name || 'Farm Name'}
                  </div>

                  <div
                    style={{
                      width: '65%',
                      height: 1,
                      background: gold,
                      marginTop: 10
                    }}
                  />
                </>
              )}
            </div>


            {/* SALE RIBBON */}
            <div
              style={{
                width: '84%',
                marginTop: 10,
                padding: '13px 30px',
                boxSizing: 'border-box',
                background: burgundy,
                color: '#fff',
                fontSize: 23,
                letterSpacing: 3,
                textTransform: 'uppercase',
                clipPath: 'polygon(4% 0,96% 0,100% 50%,96% 100%,4% 100%,0 50%)'
              }}
            >
              {banner}
            </div>


            {/* HORSE NAME */}
            <div
              style={{
                marginTop: 22,
                fontSize: nameFontSize,
                whiteSpace: 'nowrap',
                lineHeight: 1,
                fontWeight: 400,
                fontStyle: 'italic'
              }}
            >
              {horse.name}
            </div>


            {/* META */}
            <div
              style={{
                marginTop: 15,
                fontFamily: 'Arial, Helvetica, sans-serif',
                fontSize: 14,
                letterSpacing: 1.7,
                color: '#39352F'
              }}
            >
              {meta}
            </div>


            {/* PEDIGREE */}
            <div
              style={{
                width: '88%',
                marginTop: 26,
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                border: `2px solid ${gold}`,
                color: '#201E1A'
              }}
            >

              <div
                style={{
                  padding: '16px 18px',
                  borderRight: `1px solid ${gold}`
                }}
              >
                <div
                  style={{
                    color: green,
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: 2,
                    textDecoration: 'underline',
                    textUnderlineOffset: 4
                  }}
                >
                  SIRE
                </div>

                <div
                  style={{
                    marginTop: 9,
                    fontSize: 18,
                    fontWeight: 600,
                    textTransform: 'uppercase'
                  }}
                >
                  {horse.sire || '—'}
                </div>
              </div>


              <div style={{ padding: '16px 18px' }}>
                <div
                  style={{
                    color: green,
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: 2,
                    textDecoration: 'underline',
                    textUnderlineOffset: 4
                  }}
                >
                  DAM
                </div>

                <div
                  style={{
                    marginTop: 9,
                    fontSize: 18,
                    fontWeight: 600,
                    textTransform: 'uppercase'
                  }}
                >
                  {horse.dam || '—'}
                </div>
              </div>

            </div>


            {/* OWNER */}
            <div
              style={{
                marginTop: 19,
                fontFamily: 'Arial, Helvetica, sans-serif',
                fontSize: 14,
                letterSpacing: 1.3,
                color: '#292621'
              }}
            >
              <strong>OWNER:</strong>{' '}
              {(card.owner_name || horse.barn_name || '—').toUpperCase()}
            </div>

          </section>


          {/* RIGHT QR PANEL */}
          <aside
            style={{
              margin: '36px 28px 28px 0',
              padding: '25px 18px',
              borderLeft: `1px solid ${gold}`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center'
            }}
          >

            <div
              style={{
                fontSize: 29,
                letterSpacing: 1.5
              }}
            >
              INTERESTED?
            </div>

            <div
              style={{
                width: 185,
                height: 1,
                background: gold,
                margin: '13px 0'
              }}
            />

            <div
              style={{
                maxWidth: 220,
                fontFamily: 'Arial, Helvetica, sans-serif',
                color: '#403B35',
                fontSize: 13,
                lineHeight: 1.5
              }}
            >
              Scan to view this horse's public page with photos, videos and pedigree.
            </div>

            <div
              style={{
                marginTop: 24,
                width: 224,
                height: 224,
                padding: 12,
                boxSizing: 'border-box',
                background: '#fff',
                border: `2px solid ${gold}`
              }}
            >
              <QRCodeSVG
                value={publicUrl}
                level="M"
                size={198}
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'block'
                }}
              />
            </div>

            <div
              style={{
                marginTop: 20,
                fontSize: 20,
                fontStyle: 'italic',
                color: '#302C27'
              }}
            >
              Learn more about me!
            </div>

            <div
              style={{
                marginTop: 7,
                fontFamily: 'Arial, Helvetica, sans-serif',
                fontSize: 10,
                letterSpacing: .7,
                color: '#59534C'
              }}
            >
              SCAN WITH YOUR PHONE CAMERA
            </div>

          </aside>

        </div>


        {/* FOOTER */}
        <footer
          style={{
            background: green,
            color: '#fff',
            borderTop: `2px solid ${gold}`,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1.2fr'
          }}
        >

          <div
            style={{
              padding: '18px 24px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            {card.show_emergency && (
              <>
                <div
                  style={{
                    color: '#E9CF83',
                    fontSize: 11,
                    letterSpacing: 1.4
                  }}
                >
                  IN CASE OF EMERGENCY
                </div>

                <div
                  style={{
                    marginTop: 5,
                    fontFamily: 'Arial, Helvetica, sans-serif',
                    fontSize: 13,
                    lineHeight: 1.4
                  }}
                >
                  {card.emergency_name || 'Barn Contact'}

                  {card.emergency_phone && (
                    <>
                      <br />
                      {card.emergency_phone}
                    </>
                  )}
                </div>
              </>
            )}
          </div>


          <div
            style={{
              padding: '18px 24px',
              borderLeft: `1px solid ${gold}`,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}
          >
            {card.show_vet && (
              <>
                <div
                  style={{
                    color: '#E9CF83',
                    fontSize: 11,
                    letterSpacing: 1.4
                  }}
                >
                  VETERINARIAN
                </div>

                <div
                  style={{
                    marginTop: 5,
                    fontFamily: 'Arial, Helvetica, sans-serif',
                    fontSize: 13,
                    lineHeight: 1.4
                  }}
                >
                  {horse.vet_name || 'See barn office'}

                  {horse.vet_phone && (
                    <>
                      <br />
                      {horse.vet_phone}
                    </>
                  )}
                </div>
              </>
            )}
          </div>


          <div
            style={{
              padding: '18px 24px',
              borderLeft: `1px solid ${gold}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 14
            }}
          >

            <div
              style={{
                width: 46,
                height: 46,
                border: `1px solid ${gold}`,
                borderRadius: '50%',
                color: '#E9CF83',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 24
              }}
            >
              CR
            </div>

            <div style={{ textAlign: 'left' }}>
              <div
                style={{
                  color: '#E9CF83',
                  fontSize: 11,
                  letterSpacing: 1.4
                }}
              >
                LISTED VIA
              </div>

              <div
                style={{
                  marginTop: 3,
                  fontSize: 15,
                  letterSpacing: 1
                }}
              >
                THE CONNECTED RIDER
              </div>

              <div
                style={{
                  marginTop: 3,
                  fontFamily: 'Arial, Helvetica, sans-serif',
                  fontSize: 12
                }}
              >
                app.theconnectedrider.com
              </div>
            </div>

          </div>

        </footer>

      </div>
    </div>
  );
}

const STUD_COLOURS = {
  'black-silver': { paper: '#EBEBEC', dark: '#131316', gold: '#B9BEC3', banner: '#0F4A38' },
  emerald:        { paper: '#EDEFEA', dark: '#12291E', gold: '#B6C6B8', banner: '#0F4A38' },
  navy:           { paper: '#EAECF1', dark: '#0F1B30', gold: '#B0BCD0', banner: '#0F4A38' },
  plum:           { paper: '#F0EAEE', dark: '#2A0E22', gold: '#C7AEBE', banner: '#0F4A38' },
  copper:         { paper: '#F2ECE4', dark: '#2A1A0F', gold: '#CBB094', banner: '#0F4A38' },
};

export default function StudCardPage() {
  const { id } = useParams();
  const [step, setStep] = useState(1);
  const [sweepsMark, setSweepsMark] = useState('none');
  const [cardStyle, setCardStyle] = useState('black-silver');
  const [cardColours, setCardColours] = useState(STUD_COLOURS['black-silver']);
  const [stud, setStud] = useState({ booking_fee: '', live_foal_guarantee: '', semen_types: '', collection_days: '', shipping_cost: '', container_deposit: '', breeding_soundness: '', mare_requirements: '', get_record: '', contract_url: '' });
  const saveStud = async () => {
    try { await api.put('/horses/' + id, stud); }
    catch (e) { toast.error('Could not save breeding terms'); }
  };
  const [temperament, setTemperament] = useState('');
  const [salePhotos, setSalePhotos] = useState([]);
  const [damSire, setDamSire] = useState('');
  const [pedigreeUrl, setPedigreeUrl] = useState('');
  const [breederName, setBreederName] = useState('');
  const navigate = useNavigate();
  const [horse, setHorse] = useState(null);
  const [listing, setListing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [orgs, setOrgs] = useState([]);

  // Stall card customizer — separate from the public listing.
  const [card, setCard] = useState({
    template_id: 'classic-emerald',
    farm_name: '',
    farm_logo_url: '',
    owner_name: '',
    emergency_name: '',
    emergency_phone: '',
    show_emergency: true,
    show_vet: true,
    paper_color: '#FBF8F3',
    dark_color: '#1F4D3A',
    gold_color: '#BA7517',
    sale_color: '#6E1230'
  });

  const previewWrapRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(1);

  useEffect(() => {
    const el = previewWrapRef.current;
    if (!el) return;

    const updateScale = () => {
      const available = el.clientWidth || 1056;
      setPreviewScale(Math.min(1, available / 1056));
    };

    updateScale();

    const observer = new ResizeObserver(updateScale);
    observer.observe(el);

    window.addEventListener('resize', updateScale);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateScale);
    };
  }, [listing]);

  const [form, setForm] = useState({
    listing_type: 'at_stud', price_text: '', headline: '', description: '',
    videos: '', contact_phone: '', contact_email: ''
  });

  const load = useCallback(async () => {
    try {
      const [{ data: h }, { data: l }] = await Promise.all([
        api.get(`/horses/${id}`),
        api.get(`/listings/horses/${id}`)
      ]);
      setHorse(h);
        setSweepsMark(h.sweepstakes_mark || 'none');
        setCardStyle(h.card_style || 'black-silver');
        setCardColours(h.card_colors || STUD_COLOURS[h.card_style] || STUD_COLOURS['black-silver']);
        setStud({ booking_fee: h.booking_fee || '', live_foal_guarantee: h.live_foal_guarantee || '', semen_types: h.semen_types || '', collection_days: h.collection_days || '', shipping_cost: h.shipping_cost || '', container_deposit: h.container_deposit || '', breeding_soundness: h.breeding_soundness || '', mare_requirements: h.mare_requirements || '', get_record: h.get_record || '', contract_url: h.contract_url || '' });
        setTemperament(h.temperament || '');
        setSalePhotos(Array.isArray(h.sale_photos) ? h.sale_photos : []);
        setPedigreeUrl(h.pedigree_url || '');
        setDamSire(h.dam_sire || '');
        setBreederName(h.breeder || '');

      // Load farm/barn branding for the stall card customizer.
      try {
        const { data: orgData } = await api.get('/organizations/mine');
        const activeOrgs = Array.isArray(orgData)
          ? orgData.filter(o => o.my_status === 'active')
          : [];

        setOrgs(activeOrgs);

        // Prefer an organization whose name matches the horse's barn.
        // Otherwise use the first active organization.
        const preferredOrg =
          activeOrgs.find(o => o.name === h.barn_name) ||
          activeOrgs[0] ||
          null;

        setCard(c => ({
          ...c,
          farm_name: preferredOrg?.name || h.barn_name || '',
          farm_logo_url: preferredOrg?.logo_url || '',
          owner_name: h.barn_name || '',
          emergency_name: preferredOrg?.name || '',
          emergency_phone: preferredOrg?.phone || ''
        }));
      } catch {
        // The stall card can still be customized manually without an org.
        setCard(c => ({
          ...c,
          farm_name: h.barn_name || '',
          owner_name: h.barn_name || ''
        }));
      }

      if (l) {
        setListing(l);
        setForm({
          listing_type: 'at_stud',
          price_text: l.price_text || '', headline: l.headline || '', description: l.description || '',
          videos: (Array.isArray(l.video_urls) ? l.video_urls : []).join('\n'),
          contact_phone: l.contact_phone || '', contact_email: l.contact_email || ''
        });
      }
    } catch (e) {
      toast.error('Failed to load horse');
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.contact_phone.trim() && !form.contact_email.trim()) {
      toast.error('Add a contact phone or email — buyers need a way to reach you.');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/listings/horses/${id}`, {
        listing_type: form.listing_type,
        price_text: form.price_text.trim() || undefined,
        headline: form.headline.trim() || undefined,
        description: form.description.trim() || undefined,
        video_urls: form.videos.split('\n').map(s => s.trim()).filter(Boolean),
        contact_phone: form.contact_phone.trim() || undefined,
        contact_email: form.contact_email.trim() || undefined
      });
      setListing(data);
      toast.success('Listing saved — QR is live. Public page expires in 90 days.');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to save listing');
    } finally {
      setSaving(false);
    }
  };

  const disable = async () => {
    if (!listing) return;
    if (!window.confirm('Take this listing down? Printed QR codes will show an expired page.')) return;
    try {
      await api.delete(`/listings/${listing.id}`);
      setListing(null);
      toast.success('Listing taken down.');
    } catch (e) { toast.error('Failed to take down'); }
  };

  if (!horse) return <div style={{ padding: 20, color: 'var(--text-secondary)' }}>Loading…</div>;

  const publicUrl = listing ? `${window.location.origin}/api/listings/h/${listing.public_token}` : '';
  const forSale = false;   // this page is the stud card
  const banner = forSale ? 'THIS HORSE IS FOR SALE' : 'AVAILABLE FOR BREEDING';
  const bannerColor = forSale ? '#6E1230' : '#0F4A38';
  const foaled = horse.date_of_birth ? new Date(horse.date_of_birth + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : null;
  const breedLine = [horse.date_of_birth ? String(horse.date_of_birth).slice(0, 4) : null, horse.color, horse.gender, horse.breed]
    .filter(Boolean).join(' \u00b7 ').toUpperCase();

  return (
    <div>
      <div className="no-print">
        <button className="btn btn-sm btn-secondary" style={{ margin: '10px 0' }} onClick={() => navigate(-1)}>&#8592; Back</button>

        <div className="cr-stud-steps" style={{ display: 'flex', alignItems: 'center', gap: 6,
             margin: '2px 0 12px', flexWrap: 'wrap' }}>
          {[[1, 'Details'], [2, 'Design'], [3, 'Review']].map(([n, label], idx) => (
            <React.Fragment key={n}>
              {idx > 0 && <span style={{ flex: 1, minWidth: 12, height: 1, background: 'var(--border-color, #EDE7DC)' }} />}
              <button type="button" onClick={() => setStep(n)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 0,
                         padding: 0, cursor: 'pointer', fontSize: 12.5,
                         fontWeight: step === n ? 600 : 400,
                         color: step === n ? 'var(--cr-primary)' : 'var(--text-secondary)' }}>
                <span style={{ width: 20, height: 20, borderRadius: '50%', fontSize: 11,
                               display: 'flex', alignItems: 'center', justifyContent: 'center',
                               background: step === n ? 'var(--cr-primary)' : (step > n ? '#2C5A2C' : 'var(--bg-tertiary, #EDE7DC)'),
                               color: (step === n || step > n) ? '#fff' : 'var(--text-secondary)' }}>
                  {step > n ? '\u2713' : n}
                </span>
                {label}
              </button>
            </React.Fragment>
          ))}
        </div>
        {step === 1 && (
        <div className="card">
          <div className="section-label">Stud listing &amp; public page</div>
          {horse && horse.gender && !/stallion|colt/i.test(horse.gender) && (
            <div className="cr-stud-warn" style={{ display: 'flex', gap: 9, alignItems: 'flex-start',
                 background: '#FDF3DC', border: '1px solid #E9C978', borderRadius: 9,
                 padding: '10px 12px', margin: '10px 0 4px' }}>
              <span style={{ fontSize: 15, lineHeight: 1 }}>&#9888;</span>
              <span style={{ fontSize: 12.5, color: '#6B5320' }}>
                <strong style={{ display: 'block', color: '#7A5A12' }}>
                  {horse.name || 'This horse'} is recorded as a {String(horse.gender).toLowerCase()}.
                </strong>
                A stud listing advertises a stallion at stud. If that is wrong, change the sex on the
                horse record — otherwise this will read oddly to mare owners.
              </span>
            </div>
          )}
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
            Saving creates {horse.name}'s public page (photos, videos, tap-to-reveal contact). The card's QR opens it. Listings expire after 90 days.
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            <div className="cr-fixed-type" style={{ display: 'flex', alignItems: 'center', gap: 8,
                 fontSize: 13, color: 'var(--text-secondary)', marginBottom: 2 }}>
              <span style={{ background: 'var(--bg-tertiary, #F3EEE6)', borderRadius: 9,
                             padding: '3px 10px', fontWeight: 600,
                             color: 'var(--text-primary, #2C2C2A)' }}>Standing at stud</span>
              <span>set by the card you chose</span>
            </div>
            <input className="form-input" placeholder={forSale ? 'Price (e.g. $28,500)' : 'Stud fee (e.g. $1,500)'} value={form.price_text} onChange={e => setForm(f => ({ ...f, price_text: e.target.value }))} />
            <input className="form-input" placeholder="Headline (one line buyers see first)" value={form.headline} onChange={e => setForm(f => ({ ...f, headline: e.target.value }))} />
            <textarea className="form-input" rows={3} placeholder="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            <textarea className="form-input" rows={2} placeholder={'YouTube or Vimeo links, one per line'} value={form.videos} onChange={e => setForm(f => ({ ...f, videos: e.target.value }))} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <input className="form-input" placeholder="Contact phone" value={form.contact_phone} onChange={e => setForm(f => ({ ...f, contact_phone: e.target.value }))} />
              <input className="form-input" placeholder="Contact email" value={form.contact_email} onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))} />
            </div>
            <div style={{ marginTop: 4 }}>
              <label style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
                Sale photos ({salePhotos.length}/8) — first one leads the listing
              </label>
              {salePhotos.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                  {salePhotos.map((u, i) => (
                    <div key={u} style={{ position: 'relative' }}>
                      <img src={u} alt="" style={{ width: 78, height: 78, objectFit: 'cover', borderRadius: 6, display: 'block', border: i === 0 ? '2px solid var(--brand, #4A1229)' : '1px solid #ddd' }} />
                      <div style={{ display: 'flex', gap: 4, marginTop: 3 }}>
                        {i !== 0 && (
                          <button type="button" style={{ fontSize: 10, padding: '2px 5px' }}
                            onClick={async () => {
                              const next = [u, ...salePhotos.filter(x => x !== u)];
                              try { const r = await api.put('/listing-photos/' + id, { sale_photos: next }); setSalePhotos(r.data.sale_photos); }
                              catch (e) { toast.error('Could not reorder'); }
                            }}>make first</button>
                        )}
                        <button type="button" style={{ fontSize: 10, padding: '2px 5px' }}
                          onClick={async () => {
                            try { const r = await api.delete('/listing-photos/' + id + '?url=' + encodeURIComponent(u)); setSalePhotos(r.data.sale_photos); }
                            catch (e) { toast.error('Could not remove'); }
                          }}>remove</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {salePhotos.length < 8 && (
                <input type="file" accept="image/*" multiple className="form-input"
                  onChange={async e => {
                    const files = Array.from(e.target.files || []);
                    if (!files.length) return;
                    const fd = new FormData();
                    files.slice(0, 8 - salePhotos.length).forEach(f => fd.append('photos', f));
                    try {
                      const r = await api.post('/listing-photos/' + id, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      setSalePhotos(r.data.sale_photos);
                      toast.success('Photos uploaded');
                    } catch (err) { toast.error('Upload failed'); }
                    e.target.value = '';
                  }} />
              )}
            </div>


          </div>
        </div>
        )}
        {(step === 1 || step === 2) && (
          <>
            {step === 1 && (
            <div className="card cr-breeding-step1" style={{ marginTop: 10 }}>
              <div className="section-label" style={{ marginBottom: 8 }}>Breeding terms</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Booking fee</label>
                  <input className="form-input" value={stud.booking_fee} placeholder="e.g. $250"
                    onChange={e => setStud(s => ({ ...s, booking_fee: e.target.value }))}
                    onBlur={() => saveStud()} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Live foal guarantee</label>
                  <input className="form-input" value={stud.live_foal_guarantee} placeholder="e.g. LFG to 30 days"
                    onChange={e => setStud(s => ({ ...s, live_foal_guarantee: e.target.value }))}
                    onBlur={() => saveStud()} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Semen available</label>
                  <input className="form-input" value={stud.semen_types} placeholder="e.g. Cooled, fresh on farm"
                    onChange={e => setStud(s => ({ ...s, semen_types: e.target.value }))}
                    onBlur={() => saveStud()} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Collection days</label>
                  <input className="form-input" value={stud.collection_days} placeholder="e.g. Mon, Wed, Fri"
                    onChange={e => setStud(s => ({ ...s, collection_days: e.target.value }))}
                    onBlur={() => saveStud()} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Shipping</label>
                  <input className="form-input" value={stud.shipping_cost} placeholder="e.g. $350 overnight"
                    onChange={e => setStud(s => ({ ...s, shipping_cost: e.target.value }))}
                    onBlur={() => saveStud()} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Container deposit</label>
                  <input className="form-input" value={stud.container_deposit} placeholder="e.g. $250 refundable"
                    onChange={e => setStud(s => ({ ...s, container_deposit: e.target.value }))}
                    onBlur={() => saveStud()} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Breeding soundness</label>
                  <input className="form-input" value={stud.breeding_soundness} placeholder="e.g. BSE Feb 2026 · EVA negative"
                    onChange={e => setStud(s => ({ ...s, breeding_soundness: e.target.value }))}
                    onBlur={() => saveStud()} />
                </div>
              </div>
              <div style={{ marginTop: 10 }}>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Mare requirements</label>
                <textarea className="form-input" rows={2} value={stud.mare_requirements} placeholder="Culture and cytology within 30 days, current negative EVA…"
                  onChange={e => setStud(s => ({ ...s, mare_requirements: e.target.value }))}
                  onBlur={() => saveStud()} />
              </div>
              <div style={{ marginTop: 10 }}>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Get record</label>
                <textarea className="form-input" rows={2} value={stud.get_record} placeholder="Foals to date and what they have won"
                  onChange={e => setStud(s => ({ ...s, get_record: e.target.value }))}
                  onBlur={() => saveStud()} />
              </div>
              <div style={{ marginTop: 10 }}>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Breeding contract (PDF)</label>
                {stud.contract_url ? (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <a href={stud.contract_url} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>View contract</a>
                    <button type="button" className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }}
                      onClick={async () => {
                        try { await api.delete('/stud-contract/' + id); setStud(s => ({ ...s, contract_url: '' })); }
                        catch (e) { toast.error('Could not remove contract'); }
                      }}>Remove</button>
                  </div>
                ) : (
                  <input type="file" accept="application/pdf" className="form-input"
                    onChange={async e => {
                      const f = e.target.files && e.target.files[0];
                      if (!f) return;
                      const fd = new FormData();
                      fd.append('contract', f);
                      try {
                        const r = await api.post('/stud-contract/' + id, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                        setStud(s => ({ ...s, contract_url: r.data.contract_url || '' }));
                        toast.success('Contract uploaded');
                      } catch (err) { toast.error('Upload failed'); }
                    }} />
                )}
              </div>
            </div>

            )}

        {(step === 1 || step === 2) && (
          <div className="card cr-ungated" style={{ marginTop: 12 }}>
            <div className="section-label">{step === 2 ? 'Design the card' : 'Card details'}</div>
            <div className="cr-sweeps-mark" style={{ margin: '12px 0' }}>
              <div style={{ fontSize: 12, marginBottom: 4 }}>Arabian Breeders Sweepstakes</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                {[['none','None'],['entry','Sweepstakes nominated'],['sire','Nominated Sire']].map(([key, label]) => (
                  <button key={key} type="button"
                    onClick={async () => {
                      setSweepsMark(key);
                      try { await api.put('/horses/' + id, { sweepstakes_mark: key }); }
                      catch (e) { toast.error('Could not save that'); }
                    }}
                    style={{ borderRadius: 9, padding: '6px 12px', fontSize: 12.5, cursor: 'pointer',
                             background: sweepsMark === key ? 'var(--cr-primary)' : '#fff',
                             color: sweepsMark === key ? '#fff' : 'var(--text-primary, #2C2C2A)',
                             border: '1px solid ' + (sweepsMark === key ? 'var(--cr-primary)' : 'var(--border-color, #E4DCCE)') }}>
                    {label}
                  </button>
                ))}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                Prints the matching mark on the card. Only choose one your horse is genuinely
                enrolled for &mdash; you are responsible for using AHA's marks within their guidelines.
              </div>
            </div>


            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
              Customize the physical 8.5 × 11 landscape stall card. These settings affect the printed card only — they do not change the horse record.
            </div>

            <div style={{ display: 'grid', gap: 12 }}>
              {step === 1 && (<>

              

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                  Dam's sire
                </label>
                <input
                  className="form-input"
                  value={damSire}
                  placeholder="e.g. AM Power Raid +++/"
                  onChange={e => setDamSire(e.target.value)}
                  onBlur={async () => {
                    try { await api.put('/horses/' + id, { dam_sire: damSire }); }
                    catch (e) { toast.error("Could not save dam's sire"); }
                  }}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                  Temperament
                </label>
                <input
                  className="form-input"
                  value={temperament}
                  placeholder="e.g. Quiet, willing, good for a confident junior"
                  onChange={e => setTemperament(e.target.value)}
                  onBlur={async () => {
                    try { await api.put('/horses/' + id, { temperament }); }
                    catch (e) { toast.error('Could not save temperament'); }
                  }}
                />
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                  Height and weight come from the horse record and weight log
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                  Pedigree PDF
                </label>
                {pedigreeUrl ? (
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                    <a href={pedigreeUrl} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>View uploaded pedigree</a>
                    <button type="button" className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 10px' }}
                      onClick={async () => {
                        try { await api.delete('/pedigree/' + id); setPedigreeUrl(''); }
                        catch (e) { toast.error('Could not remove pedigree'); }
                      }}>Remove</button>
                  </div>
                ) : (
                  <input type="file" accept="application/pdf" className="form-input"
                    onChange={async e => {
                      const f = e.target.files && e.target.files[0];
                      if (!f) return;
                      const fd = new FormData();
                      fd.append('pedigree', f);
                      try {
                        const r = await api.post('/pedigree/' + id, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                        setPedigreeUrl(r.data.pedigree_url || '');
                        toast.success('Pedigree uploaded');
                      } catch (err) { toast.error('Upload failed — PDF only, under 10MB'); }
                    }} />
                )}
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                  Shown on the public listing as "View full pedigree"
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                  Breeder
                </label>
                <input
                  className="form-input"
                  value={breederName}
                  placeholder="who bred this horse"
                  onChange={e => setBreederName(e.target.value)}
                  onBlur={async () => {
                    try { await api.put('/horses/' + id, { breeder: breederName }); }
                    catch (e) { toast.error("Could not save breeder"); }
                  }}
                />
              </div>


              {orgs.length > 0 && (
                <div>
                  <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                    Farm / organization
                  </label>

                  <select
                    className="form-input"
                    value={orgs.find(o => o.name === card.farm_name)?.id || ''}
                    onChange={e => {
                      const org = orgs.find(o => String(o.id) === e.target.value);

                      if (!org) return;

                      setCard(c => ({
                        ...c,
                        farm_name: org.name || '',
                        farm_logo_url: org.logo_url || '',
                        emergency_name: org.name || '',
                        emergency_phone: org.phone || c.emergency_phone
                      }));
                    }}
                  >
                    <option value="">Choose organization</option>

                    {orgs.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 10
              }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                    Farm name on card
                  </label>

                  <input
                    className="form-input"
                    value={card.farm_name}
                    onChange={e => setCard(c => ({
                      ...c,
                      farm_name: e.target.value
                    }))}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                    Owner shown on card
                  </label>

                  <input
                    className="form-input"
                    value={card.owner_name}
                    onChange={e => setCard(c => ({
                      ...c,
                      owner_name: e.target.value
                    }))}
                  />
                </div>
              </div>

              {card.farm_logo_url && (
                <div>
                  <div style={{ fontSize: 12, marginBottom: 5 }}>
                    Farm logo
                  </div>

                  <div style={{
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    padding: 10,
                    background: '#fff',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: 180,
                    minHeight: 70
                  }}>
                    <img
                      src={card.farm_logo_url}
                      alt={card.farm_name || 'Farm logo'}
                      style={{
                        maxWidth: 220,
                        maxHeight: 70,
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                </div>
              )}

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 10
              }}>
                <div>
                  <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                    Emergency contact name
                  </label>

                  <input
                    className="form-input"
                    value={card.emergency_name}
                    onChange={e => setCard(c => ({
                      ...c,
                      emergency_name: e.target.value
                    }))}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                    Emergency phone
                  </label>

                  <input
                    className="form-input"
                    value={card.emergency_phone}
                    onChange={e => setCard(c => ({
                      ...c,
                      emergency_phone: e.target.value
                    }))}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={card.show_emergency}
                    onChange={e => setCard(c => ({
                      ...c,
                      show_emergency: e.target.checked
                    }))}
                  />
                  Show emergency contact
                </label>

                <label style={{ display: 'flex', gap: 7, alignItems: 'center', fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={card.show_vet}
                    onChange={e => setCard(c => ({
                      ...c,
                      show_vet: e.target.checked
                    }))}
                  />
                  Show veterinarian
                </label>
              </div>

              </>)}
              {step === 2 && (<>
                <div className="cr-stud-colours">
                  <div style={{ fontSize: 12, marginBottom: 3 }}>Card colours</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 9 }}>
                    These recolour the card you download. Pick a style below to start from, then adjust.
                  </div>
                  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                    {[['paper', 'Paper'], ['dark', 'Panel'], ['gold', 'Accent'], ['banner', 'Banner']].map(([key, label]) => (
                      <label key={key} style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                        <span style={{ display: 'block', marginBottom: 4 }}>{label}</span>
                        <input type="color" value={cardColours[key]}
                          onChange={e => setCardColours(c => ({ ...c, [key]: e.target.value }))}
                          onBlur={async () => {
                            try { await api.put('/horses/' + id, { card_colors: cardColours }); }
                            catch (err) { toast.error('Could not save the colours'); }
                          }}
                          style={{ width: 64, height: 38, padding: 0, borderRadius: 7,
                                   border: '1px solid var(--border-color, #DDD6CC)', background: 'none', cursor: 'pointer' }} />
                      </label>
                    ))}
                  </div>
                  {(() => {
                    const lum = (h) => { const v = String(h || '').replace('#',''); if (v.length !== 6) return null;
                      const r = parseInt(v.slice(0,2),16)/255, g = parseInt(v.slice(2,4),16)/255, b = parseInt(v.slice(4,6),16)/255;
                      return 0.299*r + 0.587*g + 0.114*b; };
                    const p = lum(cardColours.paper), d = lum(cardColours.dark);
                    if (p === null || d === null || p - d > 0.25) return null;
                    return (
                      <div style={{ background: '#FDF3DC', border: '1px solid #E9C978', borderRadius: 9,
                                    padding: '9px 12px', fontSize: 12, color: '#6B5320', margin: '10px 0 0' }}>
                        <strong style={{ color: '#7A5A12' }}>These colours will print badly.</strong>{' '}
                        Paper needs to stay light and Panel dark — the card's text depends on that
                        contrast. Pick a style below to start again.
                      </div>
                    );
                  })()}

                </div>
                <div className="cr-stud-styles">
                  <div style={{ fontSize: 12, marginBottom: 7 }}>Choose a style</div>
                  <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 9 }}>
                    These are the actual cards. What you see is what downloads.
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 10 }}>
                    {[['black-silver', 'Black & Silver'], ['emerald', 'Emerald'], ['navy', 'Navy'],
                      ['plum', 'Plum'], ['copper', 'Copper']].map(([key, label]) => (
                      <button key={key} type="button"
                        onClick={async () => {
                          setCardStyle(key); setCardColours(STUD_COLOURS[key]);
                          try { await api.put('/horses/' + id, { card_style: key, card_colors: STUD_COLOURS[key] }); }
                          catch (e) { toast.error('Could not save that'); }
                        }}
                        style={{ padding: 6, background: '#fff', cursor: 'pointer', borderRadius: 9,
                                 border: '2px solid ' + (cardStyle === key ? 'var(--cr-primary)' : 'var(--border-color, #E4DCCE)') }}>
                        <CardPreview style={key} colours={cardStyle === key ? cardColours : STUD_COLOURS[key]}
                          alt={label} maxWidth={400} />
                        <div style={{ fontSize: 11.5, fontWeight: 600, marginTop: 5 }}>{label}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </>)}
              {false && (<>
              <div>
                <div style={{ fontSize: 12, marginBottom: 7 }}>
                  Card colors
                </div>

                <div style={{
                  display: 'flex',
                  gap: 16,
                  flexWrap: 'wrap'
                }}>
                  <label style={{ fontSize: 12 }}>
                    Paper
                    <input
                      type="color"
                      value={card.paper_color}
                      onChange={e => setCard(c => ({
                        ...c,
                        paper_color: e.target.value
                      }))}
                      style={{ display: 'block', width: 48, height: 34, marginTop: 4 }}
                    />
                  </label>

                  <label style={{ fontSize: 12 }}>
                    Dark
                    <input
                      type="color"
                      value={card.dark_color}
                      onChange={e => setCard(c => ({
                        ...c,
                        dark_color: e.target.value
                      }))}
                      style={{ display: 'block', width: 48, height: 34, marginTop: 4 }}
                    />
                  </label>

                  <label style={{ fontSize: 12 }}>
                    Gold
                    <input
                      type="color"
                      value={card.gold_color}
                      onChange={e => setCard(c => ({
                        ...c,
                        gold_color: e.target.value
                      }))}
                      style={{ display: 'block', width: 48, height: 34, marginTop: 4 }}
                    />
                  </label>

                  <label style={{ fontSize: 12 }}>
                    Banner
                    <input
                      type="color"
                      value={card.sale_color}
                      onChange={e => setCard(c => ({
                        ...c,
                        sale_color: e.target.value
                      }))}
                      style={{ display: 'block', width: 48, height: 34, marginTop: 4 }}
                    />
                  </label>
                </div>
              </div>

            
<div>
                <label style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  marginBottom: 7
                }}>
                  Choose a style
                </label>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))',
                  gap: 8
                }}>
                  {STALL_CARD_TEMPLATES.map(template => {
                    const selected = card.template_id === template.id;

                    return (
                      <button
                        key={template.id}
                        type="button"
                        onClick={() => {
                          setCard(c => ({
                            ...c,
                            template_id: template.id,
                            paper_color: template.palette.paper,
                            dark_color: template.palette.dark,
                            gold_color: template.palette.accent,
                            sale_color: template.palette.banner
                          }));
                        }}
                        style={{
                          border: selected
                            ? '3px solid #6E1230'
                            : '1px solid #D6D3CF',
                          borderRadius: 8,
                          padding: 8,
                          background: '#fff',
                          cursor: 'pointer',
                          textAlign: 'left'
                        }}
                      >
                        <StallTemplateThumb template={template} />

                        <div style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: '#252525'
                        }}>
                          {template.name}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
              </>)}
              </div>
          </div>
        )}

          </>
        )}

        {step === 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {listing ? 'Listing is live \u2014 changes save when you press update.'
                       : 'Create the listing to switch on the public page and the QR code.'}
            </span>
            <span style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-secondary" disabled={saving} onClick={save}>
                {saving ? 'Saving\u2026' : (listing ? 'Update listing' : 'Create listing + QR')}
              </button>
              <button className="btn btn-primary" onClick={() => setStep(2)}>Next: pick a design &rarr;</button>
            </span>
          </div>
        )}
        {step === 2 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
            <button className="btn btn-secondary" onClick={() => setStep(1)}>&larr; Back</button>
            <button className="btn btn-primary" onClick={() => setStep(3)}>Next: review &rarr;</button>
          </div>
        )}
        {step === 3 && (
          <div className="card" style={{ marginTop: 12 }}>
            <div className="section-label">Review &amp; publish</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>
              Publishing makes the public page live and the QR code work. You can take it down at any time.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving\u2026' : (listing ? 'Update listing' : 'Create listing + QR')}</button>
              {listing && <button className="btn btn-secondary" onClick={() => window.open(publicUrl, '_blank')}>View public page</button>}
              {listing && <button className="btn btn-secondary" onClick={() => window.print()}>Print card</button>}
              {listing && <button className="btn btn-secondary" onClick={async () => {
                try {
                  const r = await api.get('/sale-card/' + id, { responseType: 'blob' });
                  const url = URL.createObjectURL(r.data);
                  const a = document.createElement('a');
                  a.href = url; a.download = 'sale-card.pdf'; a.click();
                  URL.revokeObjectURL(url);
                } catch (e) { toast.error('Could not build the card'); }
              }}>Download printable card</button>}
              {listing && <button className="btn btn-secondary" onClick={disable}>Take down</button>}
            </div>
            {listing && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Public link: {publicUrl}</div>}
            <div style={{ marginTop: 10 }}>
              <button className="btn btn-secondary" onClick={() => setStep(2)}>&larr; Back to the card</button>
            </div>
          </div>
        )}

        {!listing && <div className="card" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>The printable card needs a live listing for its QR code \u2014 create it above or on Review — the QR needs a live page to point at.</div>}
      </div>

      {step === 3 && (
        <div className="no-print" style={{ marginTop: 14 }}>
          <div className="card">
            <div className="section-label" style={{ marginBottom: 8 }}>The card you get</div>
            <CardPreview style={cardStyle} colours={cardColours} />
            <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', textAlign: 'center', marginTop: 8 }}>
              The stallion's details, fee and QR code are filled in when you download.
            </div>
          </div>
        </div>
      )}

      <style>{`@media print {
        .no-print, nav, header, .bottom-nav { display: none !important; }
        body { background: #fff !important; }
        .sales-card-preview {
          width: 11in !important;
          height: 8.5in !important;
          margin: 0 !important;
          overflow: visible !important;
        }
        .sales-card {
          width: 11in !important;
          height: 8.5in !important;
          max-width: none !important;
          margin: 0 !important;
          transform: none !important;
          page-break-inside: avoid;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        @page { size: letter landscape; margin: 0; }
      }`}</style>
    </div>
  );
}
